/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { programService, ProgramDetails } from "../services/program.service";
import { useDebounce } from "./useDebounce";
import { useSearchParams } from "react-router-dom";

type FilterType = "ORDERS" | "RANK" | "AFFILIATE";
type SortDirection = "asc" | "desc";
type AccountType = "TEST" | "REAL" | "ALL";

interface UseProgramSearchResult {
  programs: ProgramDetails[];
  isLoading: boolean;
  error: string | null;
  total: number;
  totalPages: number;
  currentPage: number;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  loadMore: () => void;
  setCurrentPage: (page: number) => void;
  filterBy: FilterType;
  setFilterBy: (filter: FilterType) => void;
  sortDirection: SortDirection;
  setSortDirection: (direction: SortDirection) => void;
  applyFilters: () => void;
}

export const useProgramSearch = (
  initialLimit: number = 20
): UseProgramSearchResult => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [programs, setPrograms] = useState<ProgramDetails[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterBy, setFilterBy] = useState<FilterType>("RANK");
  const [searchParams] = useSearchParams();
  const [accountType, setAccountType] = useState<AccountType>(
    (searchParams.get("accountType") as AccountType) || "REAL"
  );
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const debouncedSearchTerm = useDebounce(searchTerm, 800);

  useEffect(() => {
    setAccountType(
      (searchParams.get("accountType") as AccountType) || "REAL"
    );
  }, [searchParams]);

  const fetchSearchResults = async (
    page: number = 1,
    isLoadMore: boolean = false
  ) => {
    const isMounted = true;

    setIsLoading(true);
    setError(null);

    try {
      await new Promise((resolve) => setTimeout(resolve, 100));
      const result = await programService.searchPrograms(
        searchTerm,
        page,
        initialLimit,
        filterBy,
        sortDirection.toUpperCase() as "ASC" | "DESC",
        accountType
      );

      if (!isMounted) return;
      const searchData = Array.isArray(result) ? result : result.data || [];

      if (searchData && searchData.length > 0) {
        setPrograms((prev) => {
          // For pagination, replace the data; for load more, append to existing data
          const updatedPrograms = isLoadMore
            ? [...prev, ...searchData]
            : searchData;
          return updatedPrograms;
        });
        setTotal(result.pagination.total || searchData.length || 0);
        setTotalPages(
         result.pagination.totalPages
        );
      } else {
        if (!isLoadMore) {
          setPrograms([]);
          setTotal(0);
          setTotalPages(0);
        }
      }
    } catch {
      if (isMounted) {
        setError("Failed to load programs. Please try again.");
        if (!isLoadMore) {
          setPrograms([]);
          setTotal(0);
          setTotalPages(0);
        }
      }
    } finally {
      if (isMounted) {
        setIsLoading(false);
      }
    }
  };

  // Reset page when search term, filter, or sort direction changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterBy, sortDirection]);

  // Load initial programs
  useEffect(() => {
    if (!debouncedSearchTerm) {
      fetchSearchResults(1, false);
    }
  }, [debouncedSearchTerm]);

  // Handle search and pagination
  useEffect(() => {
    if (!isLoading && (debouncedSearchTerm || currentPage > 0)) {
      fetchSearchResults(currentPage, false);
    }
  }, [debouncedSearchTerm, currentPage]);

  const loadMore = () => {
    if (debouncedSearchTerm && !isLoading && currentPage < totalPages) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchSearchResults(nextPage, true);
    }
  };

  const applyFilters = () => {
    setCurrentPage(1);
    fetchSearchResults(1, false);
  };

  return {
    programs,
    isLoading,
    error,
    total,
    totalPages,
    currentPage,
    searchTerm,
    setSearchTerm,
    loadMore,
    setCurrentPage,
    filterBy,
    setFilterBy,
    sortDirection,
    setSortDirection,
    applyFilters,
  };
};
